import { useEffect, useRef, useState } from 'react';

/**
 * Detect on-screen keyboard visibility via the visual viewport API.
 *
 * Handles the three dismiss paths on Android/iOS:
 *   1. Input blur (tap outside)      — viewport grows back.
 *   2. System back gesture           — viewport grows back, input keeps focus.
 *   3. Explicit "hide keyboard" IME  — same as (2).
 * In every case the visualViewport `resize` event fires when the IME
 * finishes animating in or out, giving us a stable signal — which is
 * why we CANNOT rely on input focus/blur alone (Android back doesn't
 * blur the focused input).
 *
 * Focus controls whether the map is hidden; viewportReduced independently
 * gates camera fitting until the physical keyboard-sized viewport restores.
 * No suppression timeout: rapid re-focus must hide the map immediately, and
 * late IME events after blur must not hide it again.
 */
export function useGuestMapKeyboard() {
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [viewportReduced, setViewportReduced] = useState(false);
  const keyboardBaselineRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const vv = window.visualViewport;
    const readHeight = () => Math.min(vv ? vv.height : window.innerHeight, window.innerHeight);
    // Seed baseline on mount so the first shrink is measured against a
    // real "no-keyboard" height.
    keyboardBaselineRef.current = readHeight();
    const check = () => {
      const current = readHeight();
      if (current > keyboardBaselineRef.current) {
        keyboardBaselineRef.current = current;
      }
      // 150 px is comfortably above browser toolbar collapse (~60-100 px)
      // so scrolling the address bar in/out never trips the keyboard state.
      const shrunk = keyboardBaselineRef.current - current > 150;
      const focused = document.activeElement?.matches('input, textarea, [contenteditable="true"]');
      setViewportReduced(shrunk);
      setKeyboardOpen(shrunk && !!focused);
    };
    if (vv) {
      vv.addEventListener('resize', check);
      vv.addEventListener('scroll', check);
    }
    window.addEventListener('resize', check);
    const onOrientation = () => {
      // Orientation swaps re-seed the baseline — the new max viewport
      // height is different.
      keyboardBaselineRef.current = 0;
      check();
    };
    window.addEventListener('orientationchange', onOrientation);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', check);
        vv.removeEventListener('scroll', check);
      }
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', onOrientation);
    };
  }, []);

  return {
    keyboardOpen, setKeyboardOpen, viewportReduced,
  };
}
